import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { BookOpen, Upload, X, Send, Clock, Ban, Image as ImageIcon, RotateCcw } from 'lucide-react';

const QuotationModal = ({ open, onClose, bookData }) => {
  const [text, setText] = useState('');
  const [imagePath, setImagePath] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const fileRef = useRef(null);

  // Local autosave key per-book: ensures content survives crashes / power-offs
  const draftKey = `quotation_draft_v1::${bookData?.title || 'untitled'}::${bookData?.author || ''}`;

  // Restore any pending draft when modal opens
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved.text === 'string' && saved.text.trim()) {
          setText(saved.text);
          if (saved.imagePath) setImagePath(saved.imagePath);
          setRestoredFromDraft(true);
          toast.info('تم استعادة مسودتك المحفوظة تلقائياً');
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Autosave on every change (small debounce)
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try {
        if (text.trim() || imagePath) {
          localStorage.setItem(draftKey, JSON.stringify({ text, imagePath, ts: Date.now() }));
        }
      } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [text, imagePath, open, draftKey]);

  const clearDraft = () => {
    try { localStorage.removeItem(draftKey); } catch {}
    setRestoredFromDraft(false);
  };

  const discardDraft = () => {
    clearDraft();
    setText('');
    setImagePath('');
    toast.success('تم حذف المسودة');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار صورة فقط');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('حجم الصورة كبير (الحد 10 ميجا)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/quotations/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImagePath(res.data.path);
      // Store drive URL too if available
      if (res.data.drive_url) {
        setImagePath(res.data.path + '|||' + res.data.drive_url);
      }
      toast.success('تم رفع الصورة');
    } catch {
      toast.error('فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (status) => {
    if (status === 'published' && !text.trim()) {
      toast.error('يرجى كتابة شيء قبل النشر');
      return;
    }
    setSubmitting(true);
    try {
      const [objPath, driveUrl] = (imagePath || '').split('|||');
      const payload = {
        title: bookData?.title || '',
        author: bookData?.author || '',
        total_pages: bookData?.total_pages || 0,
        classification: bookData?.classification || '',
        quotation_text: text,
        image_path: objPath || '',
        drive_url: driveUrl || '',
        status,
      };
      await api.post('/quotations', payload);
      if (status === 'published') {
        toast.success('تم نشر ملخص الاستفادة بنجاح');
      } else {
        toast.success('تم حفظ المهمة في ملخصاتي — أكملها متى شئت');
      }
      clearDraft();
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'حدث خطأ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white" data-testid="quotation-modal">
        <DialogHeader>
          <div className="flex items-center justify-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-primary" />
            <DialogTitle className="text-2xl font-bold text-accent">ملخص الاستفادة</DialogTitle>
          </div>
          <p className="text-center text-muted-foreground text-sm">
            ما شاء الله! أتممت <span className="font-bold text-primary">{bookData?.title}</span>
          </p>
          <p className="text-center text-muted-foreground text-sm mt-1">
            شاركنا ملخص استفادتك مما قرأت، ومن الجيد تضمين اقتباسات من الكتاب
          </p>
          {restoredFromDraft && (
            <div className="mt-3 mx-auto flex items-center gap-2 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full" data-testid="restored-draft-banner">
              <RotateCcw className="w-3.5 h-3.5" />
              <span>تم استرجاع مسودتك التلقائية</span>
              <button type="button" onClick={discardDraft} className="text-red-500 hover:text-red-700 underline mr-1" data-testid="discard-draft-btn">
                حذف
              </button>
            </div>
          )}
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Text Area */}
          <div>
            <div className="flex items-center justify-end mb-1.5">
              <span className="text-[11px] text-emerald-600 font-medium" data-testid="autosave-indicator">حفظ تلقائي ✓</span>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="اكتب ملخص استفادتك واقتباساتك المفضلة من الكتاب..."
              className="w-full min-h-[180px] p-4 rounded-xl border-2 border-primary/20 focus:border-primary bg-white resize-y text-base leading-relaxed focus:outline-none transition-colors"
              dir="rtl"
              data-testid="quotation-text-input"
            />
          </div>

          {/* Image Upload */}
          <div className="border-2 border-dashed border-primary/20 rounded-xl p-4 text-center hover:border-primary/40 transition-colors">
            {imagePath ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary">
                  <ImageIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">تم رفع صورة الكتاب</span>
                </div>
                <button onClick={() => { setImagePath(''); if (fileRef.current) fileRef.current.value = ''; }} className="text-red-400 hover:text-red-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <label className="cursor-pointer flex flex-col items-center gap-2">
                <Upload className={`w-8 h-8 ${uploading ? 'text-primary animate-pulse' : 'text-muted-foreground'}`} />
                <span className="text-sm text-muted-foreground">
                  {uploading ? 'جارٍ الرفع...' : 'رفع صورة الكتاب (اختياري)'}
                </span>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  data-testid="quotation-image-input"
                />
              </label>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-2">
            <Button
              onClick={() => handleSubmit('published')}
              disabled={submitting || uploading}
              className="h-13 text-base bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
              data-testid="quotation-publish-btn"
            >
              <Send className="w-5 h-5 ml-2" />
              {submitting ? 'جارٍ النشر...' : 'نشر الآن'}
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => handleSubmit('draft')}
                disabled={submitting || uploading}
                className="h-12 border-2 border-primary/30 hover:bg-primary/5"
                data-testid="quotation-later-btn"
              >
                <Clock className="w-4 h-4 ml-2" />
                لاحقاً
              </Button>

              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={submitting}
                className="h-12 text-muted-foreground hover:text-accent"
                data-testid="quotation-skip-btn"
              >
                <Ban className="w-4 h-4 ml-2" />
                لا أريد الكتابة
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuotationModal;
